import { useQuery } from '@tanstack/react-query';

import { previewDocumentPdf } from '../api/documentPdfApi';
import type { DocumentDraft } from './documentDraftTypes';

export const documentPdfPreviewKeys = {
  document: (patientId: string, draft: DocumentDraft | null) =>
    ['document-pdf-preview', patientId, draft?.id ?? 'none', draft?.updatedAt ?? 'none'] as const,
};

export type DocumentPdfPreviewMode = 'hidden' | 'visible';
export type DocumentPdfPreviewRefreshMode = 'cached' | 'always';

interface DocumentPdfPreviewQueryOptions {
  mode?: DocumentPdfPreviewMode;
  refresh?: DocumentPdfPreviewRefreshMode;
}

export function useDocumentPdfPreviewQuery(
  patientId: string,
  draft: DocumentDraft | null,
  options: DocumentPdfPreviewQueryOptions = {}
) {
  const mode = options.mode ?? 'visible';
  const refresh = options.refresh ?? 'cached';

  return useQuery({
    queryKey: documentPdfPreviewKeys.document(patientId, draft),
    queryFn: ({ signal }) => {
      if (draft === null) {
        throw new Error('A draft is required to create a preview.');
      }
      return previewDocumentPdf(patientId, draft, signal);
    },
    enabled: patientId !== '' && draft !== null && mode === 'visible',
    refetchOnMount: refresh === 'always' ? 'always' : undefined,
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[2] === draft?.id ? previousData : undefined,
    staleTime: refresh === 'always' ? 0 : 60_000,
    gcTime: 5 * 60_000,
  });
}
